import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CarCreateInput,
  CarDetail,
  CarListResponse,
  CarStatus,
  CarTextResponse,
  CarUpdateInput,
  CostCreateInput,
  StatusChangeInput,
  TextLength,
} from '@telega/shared';
import { request } from './client';

export const carKeys = {
  all: ['cars'] as const,
  list: (status: CarStatus | 'all', q: string) => ['cars', 'list', status, q] as const,
  detail: (id: string) => ['cars', 'detail', id] as const,
};

/** Shared by the fleet screen and the warm start in App.tsx. */
export function carsQuery(status: CarStatus | 'all', q: string) {
  const params = new URLSearchParams({ status });
  if (q.trim()) params.set('q', q.trim());
  return {
    queryKey: carKeys.list(status, q.trim()),
    queryFn: () => request<CarListResponse>(`/cars?${params}`),
  };
}

export function useCars(status: CarStatus | 'all', q: string) {
  return useQuery({ ...carsQuery(status, q), placeholderData: keepPreviousData });
}

/** How often the card asks again while the worker still has posts to send or edit. */
export const CAR_SYNC_POLL_MS = 2_500;

export function useCar(id: string | undefined) {
  return useQuery({
    queryKey: carKeys.detail(id ?? ''),
    queryFn: () => request<CarDetail>(`/cars/${id}`),
    enabled: Boolean(id),
    // A post deleted by hand or a failed edit shows up without reopening the app.
    refetchInterval: (query) => (query.state.data?.syncing ? CAR_SYNC_POLL_MS : false),
  });
}

/** Writes a fresh detail into the cache and refreshes the lists. */
function useCarCacheUpdate() {
  const qc = useQueryClient();
  return (car: CarDetail) => {
    qc.setQueryData(carKeys.detail(car.id), car);
    void qc.invalidateQueries({ queryKey: ['cars', 'list'] });
  };
}

export function useCreateCar() {
  const update = useCarCacheUpdate();
  return useMutation({
    mutationFn: (body: CarCreateInput) => request<CarDetail>('/cars', { method: 'POST', body }),
    onSuccess: update,
  });
}

export function useUpdateCar(id: string) {
  const update = useCarCacheUpdate();
  return useMutation({
    mutationFn: (body: CarUpdateInput) =>
      request<CarDetail>(`/cars/${id}`, { method: 'PATCH', body }),
    onSuccess: update,
  });
}

export function useGenerateText(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (length: TextLength) =>
      request<CarTextResponse>(`/cars/${id}/text`, { method: 'POST', body: { length } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: carKeys.detail(id) }),
  });
}

export function useChangeStatus(id: string) {
  const update = useCarCacheUpdate();
  return useMutation({
    mutationFn: (body: StatusChangeInput) =>
      request<CarDetail>(`/cars/${id}/status`, { method: 'POST', body }),
    onSuccess: update,
  });
}

/** Publication actions return the updated car card. */
export function usePublicationAction(_carId: string) {
  const update = useCarCacheUpdate();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'retry' | 'manual-done' }) =>
      request<CarDetail>(`/publications/${id}/${action}`, { method: 'POST' }),
    onSuccess: update,
  });
}

export function usePublish(carId: string) {
  const update = useCarCacheUpdate();
  return useMutation({
    mutationFn: (channelIds: string[]) =>
      request<CarDetail>(`/cars/${carId}/publish`, { method: 'POST', body: { channelIds } }),
    onSuccess: update,
  });
}

/** "Опублікувати заново" (D41): a new post in each channel, the older ones stay and update. */
export function useRepost(carId: string) {
  const update = useCarCacheUpdate();
  return useMutation({
    mutationFn: (channelIds: string[]) =>
      request<CarDetail>(`/cars/${carId}/repost`, { method: 'POST', body: { channelIds } }),
    onSuccess: update,
  });
}

/** D47: the seller's private note; an empty note removes it. */
export function useSaveNote(carId: string) {
  const update = useCarCacheUpdate();
  return useMutation({
    mutationFn: (note: string) =>
      request<CarDetail>(`/cars/${carId}/note`, { method: 'PUT', body: { note } }),
    onSuccess: update,
  });
}

export function useAddCost(carId: string) {
  const update = useCarCacheUpdate();
  return useMutation({
    mutationFn: (body: CostCreateInput) =>
      request<CarDetail>(`/cars/${carId}/costs`, { method: 'POST', body }),
    onSuccess: update,
  });
}

export function useDeleteCost(carId: string) {
  const update = useCarCacheUpdate();
  return useMutation({
    mutationFn: (costId: string) =>
      request<CarDetail>(`/cars/${carId}/costs/${costId}`, { method: 'DELETE' }),
    onSuccess: update,
  });
}
